/**
 * Messages Page — Seafarer's dedicated messaging with their linked accountant.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageSquare, Send, Loader2, Paperclip, X, Building, Briefcase, LinkIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState(false);
  const [accountantName, setAccountantName] = useState('');
  const [attachFile, setAttachFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client-link/status`);
      setLinked(res.data.linked);
      if (res.data.linked) {
        setAccountantName(res.data.accountant?.firm_name || 'Your Accountant');
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client-link/messages`);
      setMessages(res.data.messages || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (linked) {
      fetchMessages();
      const iv = setInterval(fetchMessages, 8000);
      return () => clearInterval(iv);
    } else {
      setLoading(false);
    }
  }, [linked, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setAttachFile({ name: file.name, content: reader.result.split(',')[1], type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() && !attachFile) return;
    setSending(true);
    try {
      let attachmentId = null;
      let attachmentType = null;

      if (attachFile) {
        setUploading(true);
        const docRes = await axios.post(`${API_URL}/api/documents/upload`, {
          file_name: attachFile.name,
          file_content: attachFile.content,
          document_type: 'other',
          tax_year: '',
          month: ''
        });
        attachmentId = docRes.data.document?.id;
        attachmentType = 'document';
        setUploading(false);
      }

      await axios.post(`${API_URL}/api/client-link/messages`, {
        content: newMsg.trim() || (attachFile ? `Shared file: ${attachFile.name}` : ''),
        attachment_type: attachmentType,
        attachment_id: attachmentId
      });
      setNewMsg('');
      setAttachFile(null);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); setUploading(false); }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="messages-page">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-amber-400" />Messages
        </h1>
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  if (!linked) {
    return (
      <div className="space-y-6" data-testid="messages-page">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-amber-400" />Messages
        </h1>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <Briefcase className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Accountant Linked</h3>
            <p className="text-slate-400 text-sm mb-4">
              Link your accountant first to start messaging. You can invite them or share a link code.
            </p>
            <Button
              onClick={() => navigate('/my-accountant')}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="go-link-accountant-btn"
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              Go to My Accountant
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-amber-400" />Messages
        </h1>
        <p className="text-slate-400 mt-1 text-sm flex items-center gap-1.5">
          <Building className="h-3.5 w-3.5" />
          Chatting with {accountantName}
        </p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {/* Message List */}
          <div className="h-[calc(100vh-280px)] min-h-[400px] overflow-y-auto p-4 space-y-3" data-testid="messages-list">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageSquare className="h-10 w-10 text-slate-600 mb-3" />
                <p className="text-slate-500 text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                    msg.sender_role === 'user'
                      ? 'bg-amber-600/20 border border-amber-500/20'
                      : 'bg-slate-700/50 border border-slate-600/50'
                  }`}>
                    <p className="text-xs text-slate-500 mb-0.5">
                      {msg.sender_role === 'user' ? 'You' : msg.sender_name || 'Accountant'}
                    </p>
                    <p className="text-sm text-slate-200">{msg.content}</p>
                    {msg.attachment_id && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-sky-400">
                        <Paperclip className="h-3 w-3" />
                        <span>File attached</span>
                      </div>
                    )}
                    <p className="text-[10px] text-slate-600 mt-1">
                      {new Date(msg.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Send Form */}
          <form onSubmit={sendMessage} className="border-t border-slate-700 p-3 flex items-end gap-2">
            <div className="flex-1 space-y-1">
              {attachFile && (
                <div className="flex items-center gap-2 bg-slate-700/50 rounded px-2 py-1 text-xs text-slate-300">
                  <Paperclip className="h-3 w-3 text-amber-400" />
                  <span className="truncate flex-1">{attachFile.name}</span>
                  <button type="button" onClick={() => setAttachFile(null)} className="text-slate-500 hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input value={newMsg} onChange={e => setNewMsg(e.target.value)}
                  placeholder="Type a message..." disabled={sending}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm flex-1"
                  data-testid="message-input" />
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
                <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}
                  className="text-slate-400 hover:text-amber-400 h-9 px-2" data-testid="attach-file-btn">
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Button type="submit" size="sm" disabled={sending || (!newMsg.trim() && !attachFile)}
                  className="bg-amber-600 hover:bg-amber-700 h-9 px-3" data-testid="send-message-btn">
                  {sending || uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Messages;
